import { Button } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormText } from 'components/form/FormText/FormText';
import HtmlTrans from 'components/shared/HtmlTrans';
import { Heading4 } from 'components/shared/Typography';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { FormProvider, useForm } from 'react-hook-form';
import OutOfOfficeService from 'services/out-of-office.service';
import ServiceEventTracking from 'services/service.event-tracking';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getErrorMessage } from 'utils/errors';
import { getPublicPath } from 'utils/paths';
import { z } from 'zod';
import { StyledContainer, StyledForm } from './OutOfOfficeForm.styled';

const FormFieldsSchema = z.object({
  email: z.string().email('common:invalid-email')
});

type FormFields = z.infer<typeof FormFieldsSchema>;

const OutOfOfficeForm: React.FC = () => {
  const { t } = useI18n('out-of-office');
  const router = useRouter();
  const { addErrorToast } = useToasts();

  const formMethods = useForm<FormFields>({
    defaultValues: {
      email: ''
    },
    resolver: zodResolver(FormFieldsSchema)
  });

  const { handleSubmit } = formMethods;

  const onSubmit = async (data: FormFields): Promise<void> => {
    try {
      await OutOfOfficeService.join({
        email: data.email
      });

      await router.push(getAppUrl(APP_URLS.outOfOfficeOk, router.locale));
    } catch (error) {
      console.error(
        `[OutOfOfficeForm] Could not send join to Out Of Office form. ${error}`
      );

      addErrorToast(
        t('out-of-office.form.submit-error', {
          error: getErrorMessage(error)
        }),
        t('out-of-office.form.submit-error-title')
      );
    }
  };

  return (
    <StyledContainer>
      <img src={getPublicPath('/images/outOfOffice/form.png')} alt="" />
      <div>
        <Heading4>{t('out-of-office.form.title')}</Heading4>
        <HtmlTrans
          ns={'out-of-office'}
          i18nKey={'out-of-office.form.desc'}
        ></HtmlTrans>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...formMethods}>
            <FormText
              name="email"
              formGroup={{
                label: t('out-of-office.form.email')
              }}
            />

            <Button
              schema="brand2"
              label={t('out-of-office.form.submit')}
              startIcon={faCheckCircle}
              type="submit"
              onClick={() =>
                ServiceEventTracking.trackLandingCtaClicked(
                  'out-of-office-subscribed'
                )
              }
            />
          </FormProvider>
        </StyledForm>
      </div>
    </StyledContainer>
  );
};

export default OutOfOfficeForm;
